import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './styles.module.scss';

const TYPE_ICONS = { territory: '📍', building: '🏢', floor: '▦', room: '🚪' };

export default function ObjectTree({ allObjects, selectedId, onSelect, expandNodeId, isOpen }) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});

  const roots = useMemo(() => allObjects.filter(o => !o.parent_id), [allObjects]);

  useEffect(() => {
    const init = {};
    roots.forEach(r => { init[r.id] = true; });
    setExpanded(prev => ({ ...init, ...prev }));
  }, [roots.map(r => r.id).join(',')]);

  useEffect(() => {
    if (!expandNodeId || !allObjects.length) return;
    const toExpand = {};
    let cur = allObjects.find(o => o.id === expandNodeId);
    while (cur) {
      toExpand[cur.id] = true;
      cur = cur.parent_id ? allObjects.find(o => o.id === cur.parent_id) : null;
    }
    setExpanded(prev => ({ ...prev, ...toExpand }));
  }, [expandNodeId, allObjects]);

  const toggle = useCallback((id, e) => {
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const q = query.trim().toLowerCase();

  const nodeMatchesQuery = useCallback((node) => {
    if (!q) return true;
    if (node.name?.toLowerCase().includes(q)) return true;
    if (node.description?.toLowerCase().includes(q)) return true;
    return allObjects.filter(o => o.parent_id === node.id).some(c => nodeMatchesQuery(c));
  }, [q, allObjects]);

  const renderNode = useCallback((node, level = 0) => {
    if (!nodeMatchesQuery(node)) return null;
    const children = allObjects.filter(o => o.parent_id === node.id);
    const hasMatchingDescendant = q
      ? children.some(c => nodeMatchesQuery(c))
      : false;
    const isOpen = !!expanded[node.id] || hasMatchingDescendant;
    const isActive = selectedId === node.id;

    return (
      <div key={node.id}>
        <button
          className={`${styles.treeNode} ${isActive ? styles.treeNodeActive : ''}`}
          style={{ paddingLeft: 8 + level * 14 }}
          onClick={() => onSelect(node)}
        >
          <span
            className={`${styles.treeChevron} ${children.length ? (isOpen ? styles.open : '') : styles.hidden}`}
            onClick={children.length ? (e) => toggle(node.id, e) : undefined}
          >
            ▶
          </span>
          <span className={styles.treeIcon}>{TYPE_ICONS[node.type] || '📄'}</span>
          <span className={styles.treeText}>
            <span className={styles.treeTextMain}>{node.name}</span>
            {node.description && (
              <span className={styles.treeTextSub}>{node.description}</span>
            )}
          </span>
        </button>
        {isOpen && children.map(child => renderNode(child, level + 1))}
      </div>
    );
  }, [allObjects, expanded, selectedId, nodeMatchesQuery, onSelect, toggle]);

  const visibleRoots = roots.filter(r => nodeMatchesQuery(r));

  return (
    <aside className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
      <div className={styles.sidebarHeader}>
        <div className={styles.sidebarTitle}>Дерево объектов</div>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.5" />
              <line x1="8.5" y1="8.5" x2="12" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>
          <input
            className={styles.searchInput}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Поиск по дереву…"
          />
          {query && (
            <button className={styles.searchClear} onClick={() => setQuery('')}>×</button>
          )}
        </div>
      </div>
      <div className={styles.sidebarTree}>
        {visibleRoots.length === 0 && (
          <div className={styles.treeEmpty}>Ничего не найдено</div>
        )}
        {visibleRoots.map(r => renderNode(r))}
      </div>
    </aside>
  );
}
